import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PIIValueFinding } from "../types/api";

/**
 * Per-finding decision the uploader makes in the review UI.
 *
 * - "pending"  → finding is shown but no decision yet; blocks submit.
 * - "redact"   → just this matched substring is hashed in the cell; the rest
 *                of the free-text stays intact. This is the safe default for
 *                isolated PII leaks inside otherwise-useful narrative.
 * - "dismiss"  → false positive; leave the cell as-is.
 *
 * Column-wide pseudonymization is a separate decision the uploader makes via
 * the column checkbox list — once a column is pseudonymized wholesale, every
 * finding inside it is moot and gets greyed out here.
 */
export type FindingDecision = "pending" | "redact" | "dismiss";

export interface PIIValueFindingsPanelProps {
  findings: PIIValueFinding[];
  /**
   * Columns the uploader has already chosen to pseudonymize. Findings inside
   * one of these columns are de-prioritised — the column-level mask will
   * cover them anyway.
   */
  pseudonymizedColumns?: string[];
  /**
   * Columns the uploader has chosen to drop from the upload entirely. The
   * server removes them from the CSV before storage; findings inside them
   * are moot and are shown greyed out.
   */
  droppedColumns?: string[];
  /**
   * Per-finding decision state, keyed by `${column}#${row_index}#${type}#${matched_text}`.
   */
  decisions: Record<string, FindingDecision>;
  onDecide: (key: string, decision: FindingDecision) => void;
  /** Called when the uploader hits "Pseudonymize the whole column instead" */
  onPseudonymizeColumn?: (column: string) => void;
  /** Called when the uploader chooses to remove a column from the upload entirely. */
  onDropColumn?: (column: string) => void;
  /** Called to undo a drop (button is shown when a column is currently marked for drop). */
  onRestoreColumn?: (column: string) => void;
  /** Bulk action: set every undecided finding in a column to a decision. */
  onDecideAllInColumn?: (column: string, decision: FindingDecision) => void;
  acknowledged: boolean;
  onAcknowledgeChange: (acknowledged: boolean) => void;
}

const TYPE_LABEL_KEYS: Record<string, string> = {
  kennitala: "typeLabels.kennitala",
  email: "typeLabels.email",
  phone: "typeLabels.phone",
  kt_cue: "typeLabels.ktCue",
};

const TYPE_COLORS: Record<string, string> = {
  kennitala: "#b91c1c",
  email: "#b45309",
  phone: "#1d4ed8",
  kt_cue: "#92400e",
};

export const piiFindingKey = (f: PIIValueFinding): string =>
  `${f.column}#${f.row_index}#${f.type}#${f.matched_text}`;

const buttonBase: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: "11px",
  borderRadius: "4px",
  border: "1px solid #d1d5db",
  cursor: "pointer",
  fontWeight: 500,
};

const segmentedButton = (active: boolean, accent: string): React.CSSProperties => ({
  ...buttonBase,
  backgroundColor: active ? accent : "white",
  color: active ? "white" : "#374151",
  borderColor: active ? accent : "#d1d5db",
});

/**
 * Renders the value-level PII review panel. The uploader's mental model is
 *
 *   "For each cell where the scanner found PII, what do I want to do?"
 *
 * with exactly three answers per finding:
 *   1. Hash just this value — keep the surrounding free text intact.
 *   2. Dismiss — false positive, leave the cell alone.
 *   3. (escape hatch) Pseudonymize the entire column — covers everything in
 *      the column in one go, useful when one column has many findings.
 *
 * Submit stays disabled until either every finding has a decision or the
 * column it lives in has been pseudonymized wholesale. The server re-runs
 * the same scan and refuses any upload that still carries undecided
 * findings, so this UI is the path-of-least-resistance, not the only gate.
 */
export const PIIValueFindingsPanel: React.FC<PIIValueFindingsPanelProps> = ({
  findings,
  pseudonymizedColumns = [],
  droppedColumns = [],
  decisions,
  onDecide,
  onPseudonymizeColumn,
  onDropColumn,
  onRestoreColumn,
  onDecideAllInColumn,
  acknowledged,
  onAcknowledgeChange,
}) => {
  const { t } = useTranslation("piiValueFindingsPanel");
  const groups = useMemo(() => {
    const map = new Map<string, PIIValueFinding[]>();
    for (const f of findings) {
      const list = map.get(f.column) ?? [];
      list.push(f);
      map.set(f.column, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [findings]);

  const pseudoSet = new Set(pseudonymizedColumns);
  const dropSet = new Set(droppedColumns);
  // A column is "handled at the column level" if it's been pseudonymized or
  // dropped — either covers every finding in it.
  const columnHandled = (col: string) => pseudoSet.has(col) || dropSet.has(col);
  // Pseudonymized columns disappear from the panel entirely: the uploader
  // already declared "this is PII" via the column picker above (or via the
  // panel's column-level button), and there's nothing left to review.
  // Dropped columns stay visible so the uploader can still see the Restore
  // button — that's the only undo path.
  const visibleGroups = groups.filter(([col]) => !pseudoSet.has(col));
  if (findings.length === 0 || visibleGroups.length === 0) return null;

  const pendingCount = findings.filter(
    f => !columnHandled(f.column) && (decisions[piiFindingKey(f)] ?? "pending") === "pending",
  ).length;
  const redactCount = findings.filter(
    f => !columnHandled(f.column) && decisions[piiFindingKey(f)] === "redact",
  ).length;

  return (
    <div
      style={{
        marginTop: "16px",
        padding: "16px",
        backgroundColor: "#fff5f5",
        border: "1px solid #fca5a5",
        borderRadius: "8px",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "10px" }}>
        <strong style={{ color: "#991b1b", fontSize: "14px" }}>
          {t("header", { count: findings.length })}
        </strong>
        <span style={{ fontSize: "12px", color: "#6c757d" }}>
          {pendingCount === 0
            ? t("allDecisionsMade", { count: redactCount })
            : t("pendingDecision", { count: pendingCount })}
        </span>
      </div>

      <div style={{ fontSize: "12px", color: "#7f1d1d", marginBottom: "12px", lineHeight: 1.5 }}>
        {t("instructions.intro")}
        <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
          <li>
            <strong style={{ color: "#047857" }}>{t("instructions.hashLabel")}</strong> —{" "}
            {t("instructions.hashDescription")}
          </li>
          <li>
            <strong style={{ color: "#6b7280" }}>{t("instructions.dismissLabel")}</strong> —{" "}
            {t("instructions.dismissDescription")}
          </li>
        </ul>
        <div style={{ marginTop: "8px" }}>
          {t("instructions.columnIntro")}
          <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
            <li>
              <strong style={{ color: "#1d4ed8" }}>{t("instructions.pseudonymizeLabel")}</strong> —{" "}
              {t("instructions.pseudonymizeDescription")}
            </li>
            <li>
              <strong style={{ color: "#b91c1c" }}>{t("instructions.dropLabel")}</strong> —{" "}
              {t("instructions.dropDescription")}
            </li>
          </ul>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {visibleGroups.map(([column, columnFindings]) => {
          const columnPseudonymized = pseudoSet.has(column);
          const columnDropped = dropSet.has(column);
          // Visual state for the column card: dropped wins (it's the most
          // aggressive action), then pseudonymized, then default.
          const cardBg = columnDropped ? "#fef2f2" : columnPseudonymized ? "#ecfdf5" : "white";
          const headerBg = columnDropped ? "#fecaca" : columnPseudonymized ? "#d1fae5" : "#fee2e2";
          return (
            <div
              key={column}
              style={{
                border: "1px solid #fecaca",
                borderRadius: "6px",
                backgroundColor: cardBg,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  padding: "8px 12px",
                  borderBottom: "1px solid #fecaca",
                  backgroundColor: headerBg,
                  fontSize: "13px",
                }}
              >
                <div>
                  <strong style={{ textDecoration: columnDropped ? "line-through" : "none" }}>{column}</strong>{" "}
                  <span style={{ color: "#6c757d", fontSize: "12px" }}>
                    {t("findingCount", { count: columnFindings.length })}
                  </span>
                  {columnDropped && (
                    <span style={{ marginLeft: "8px", color: "#b91c1c", fontSize: "12px" }}>
                      {t("columnDroppedNotice")}
                    </span>
                  )}
                  {!columnDropped && columnPseudonymized && (
                    <span style={{ marginLeft: "8px", color: "#047857", fontSize: "12px" }}>
                      {t("columnPseudonymizedNotice")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {columnDropped ? (
                    onRestoreColumn && (
                      <button
                        onClick={() => onRestoreColumn(column)}
                        style={{ ...buttonBase, backgroundColor: "white", color: "#b91c1c", borderColor: "#fca5a5" }}
                        title={t("restoreColumnTitle")}
                      >
                        {t("restoreColumn")}
                      </button>
                    )
                  ) : (
                    <>
                      {!columnPseudonymized && onDecideAllInColumn && (
                        <>
                          <button
                            onClick={() => onDecideAllInColumn(column, "redact")}
                            style={{ ...buttonBase, backgroundColor: "#047857", color: "white", borderColor: "#047857" }}
                            title={t("hashAllMatchedValuesTitle")}
                          >
                            {t("hashAllMatchedValues")}
                          </button>
                          <button
                            onClick={() => onDecideAllInColumn(column, "dismiss")}
                            style={{ ...buttonBase, backgroundColor: "white", color: "#374151" }}
                            title={t("dismissAllTitle")}
                          >
                            {t("dismissAll")}
                          </button>
                        </>
                      )}
                      {!columnPseudonymized && onPseudonymizeColumn && (
                        <button
                          onClick={() => onPseudonymizeColumn(column)}
                          style={{ ...buttonBase, backgroundColor: "#1d4ed8", color: "white", borderColor: "#1d4ed8" }}
                          title={t("pseudonymizeColumnTitle")}
                        >
                          {t("pseudonymizeColumn")}
                        </button>
                      )}
                      {onDropColumn && (
                        <button
                          onClick={() => onDropColumn(column)}
                          style={{ ...buttonBase, backgroundColor: "#b91c1c", color: "white", borderColor: "#b91c1c" }}
                          title={t("dropColumnTitle")}
                        >
                          {t("dropColumn")}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#6c757d", backgroundColor: "#f9fafb" }}>
                    <th style={{ padding: "6px 12px", fontWeight: 600 }}>{t("table.row")}</th>
                    <th style={{ padding: "6px 12px", fontWeight: 600 }}>{t("table.type")}</th>
                    <th style={{ padding: "6px 12px", fontWeight: 600 }}>{t("table.context")}</th>
                    <th style={{ padding: "6px 12px", fontWeight: 600, textAlign: "right", whiteSpace: "nowrap" }}>{t("table.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {columnFindings.map(f => {
                    const key = piiFindingKey(f);
                    const decision: FindingDecision = decisions[key] ?? "pending";
                    const labelKey = TYPE_LABEL_KEYS[f.type];
                    const label = labelKey ? t(labelKey) : f.type;
                    const color = TYPE_COLORS[f.type] ?? "#7c2d12";

                    const muted = columnPseudonymized || columnDropped;
                    return (
                      <tr
                        key={key}
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          opacity: muted ? 0.55 : 1,
                          backgroundColor:
                            decision === "redact" ? "#f0fdf4"
                            : decision === "dismiss" ? "#f9fafb"
                            : "transparent",
                        }}
                      >
                        <td style={{ padding: "6px 12px", color: "#6c757d", verticalAlign: "top" }}>
                          #{f.row_index + 1}
                        </td>
                        <td style={{ padding: "6px 12px", verticalAlign: "top" }}>
                          <span style={{ color, fontWeight: 600 }}>{label}</span>
                        </td>
                        <td style={{ padding: "6px 12px", verticalAlign: "top", fontFamily: "ui-monospace, monospace" }}>
                          <span style={{ color: "#374151" }}>{f.snippet_before}</span>
                          <span
                            style={
                              decision === "redact" && !muted
                                ? {
                                    backgroundColor: "#bbf7d0",
                                    color: "#065f46",
                                    padding: "0 3px",
                                    borderRadius: "2px",
                                    fontWeight: 700,
                                    letterSpacing: "1px",
                                  }
                                : decision === "dismiss" && !muted
                                ? {
                                    backgroundColor: "#bbf7d0",
                                    color: "#065f46",
                                    padding: "0 3px",
                                    borderRadius: "2px",
                                    fontWeight: 600,
                                  }
                                : {
                                    backgroundColor: "#fef08a",
                                    color: "#854d0e",
                                    padding: "0 3px",
                                    borderRadius: "2px",
                                    fontWeight: 600,
                                  }
                            }
                            // Visual confirmation only — the server still receives
                            // the original matched_text and hashes it server-side.
                            title={
                              decision === "redact" && !muted
                                ? t("willBeHashed", { value: f.matched_text })
                                : decision === "dismiss" && !muted
                                ? t("dismissedNotice")
                                : undefined
                            }
                          >
                            {decision === "redact" && !muted ? "****" : f.matched_text}
                          </span>
                          <span style={{ color: "#374151" }}>{f.snippet_after}</span>
                        </td>
                        <td style={{ padding: "6px 12px", textAlign: "right", verticalAlign: "top" }}>
                          {!muted && (
                            <div style={{ display: "inline-flex", gap: "4px" }}>
                              <button
                                onClick={() => onDecide(key, decision === "redact" ? "pending" : "redact")}
                                style={segmentedButton(decision === "redact", "#047857")}
                                title={t("hashThisValueTitle")}
                              >
                                {t("hashThisValue")}
                              </button>
                              <button
                                onClick={() => onDecide(key, decision === "dismiss" ? "pending" : "dismiss")}
                                style={segmentedButton(decision === "dismiss", "#6b7280")}
                                title={t("dismissTitle")}
                              >
                                {t("dismiss")}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          marginTop: "14px",
          padding: "10px 12px",
          backgroundColor: "white",
          border: "1px solid #fca5a5",
          borderRadius: "6px",
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={e => onAcknowledgeChange(e.target.checked)}
          style={{ marginTop: "2px" }}
        />
        <span style={{ color: "#1a1d23" }}>
          {t("acknowledge")}
          {pendingCount > 0 && (
            <span style={{ display: "block", color: "#991b1b", fontSize: "12px", marginTop: "2px" }}>
              {t("pendingWarning", { count: pendingCount })}
            </span>
          )}
          {pendingCount === 0 && redactCount > 0 && (
            <span style={{ display: "block", color: "#047857", fontSize: "12px", marginTop: "2px" }}>
              {t("redactSummary", { count: redactCount })}
            </span>
          )}
        </span>
      </label>
    </div>
  );
};
